<template>
  <div class="container">
    <div class="donate" cols="9" md="auto">
      <h1><u>Privacy Policy</u></h1>

      <p class="p-down"><i>CitSci Privacy Policy as of November 30, 2020</i></p>

      <h2>Commitment to Individual Privacy</h2>

      <p>CitSci is committed to protecting the privacy and accuracy of
        confidential information submitted by any member of the public as well as
        students and employees of Colorado State University who may visit and use
        our website and associated mobile apps. Towards this goal, <span class="text-grey-800 font-weight-bold">
          <i>we do not actively share personal information gathered</i></span>.
        However, because CitSci is part of Colorado State University, a public
        institution in Colorado, some information collected from our website and
        mobile apps may be subject to the
        <b-link v-b-tooltip.hover.left title="C.R.S. 24-72 201 et seq. It is the
      declared public policy of the State of Colorado that all public records shall
      be open for inspection by any person at reasonable times, except as otherwise
      provided by law. Public Records are defined by this law as all writings made
      or maintained by a state institution, such as Colorado State University,
      regardless of the format or medium of the records.">
          Colorado Public (Open) Records Act
        </b-link>
        .
        Thus, in some cases we may be compelled by law to release information
        gathered from our web servers. This may include server log information,
        e-mail messages sent to an individual identified on the website, and
        information collected from web-based forms.
      </p>

      <h2>Information Collected</h2>

      <p>It is our policy to collect the least amount of personally
        identifiable information needed to fulfill our duties and responsibilities,
        to complete a particular transaction, or as required by law. This policy
        applies to the collection of all personally identifiable information,
        regardless of source or medium.</p>

      <p>The CitSci website collects and maintains a list of all CitSci
        members. This list includes information about member names, emails, and
        <span class="text-grey-800 font-weight-bold"><i>optional</i></span> information such as phone,
        address, and member preferences that each member selects such as whether or not they wish to
        receive our monthly e-newsletter. You control how you would like to be
        seen on CitSci in your My Preferences section of your persoanl profile. You
        may select from full name, first and last initial, username, and anonymous
        options. Additionally, CitSci collects scientific data submitted by its members
        such as species occurrences, site characteristics of any sort, and other data
        as specified by project managers for volunteers to submit. There are privacy
        settings within CitSci that facilitate opening or closing these data to public
        access. These settings are currently set by project managers.
      </p>

      <p>For site administration, information other than personal information linked
        to a particular individual is collected for analysis and statistical purposes
        to guide website navigation and improve website user experiences. This
        information is used to help diagnose problems, assess what information is of
        most interest, determine technical design specifications, identify system
        performance and/or problem areas, and other administrative purposes.</p>

      <p>Members may choose whether or not to provide personal information to CitSci
        via the Internet. If a member chooses not to provide the personal information
        requested, they can still visit most of parts of the CitSci website, but
        may be unable to access certain options, offers, and services that involve our
        interaction with them.</p>

      <h2>Use and Disclosure of Information</h2>

      <p>As a policy, the names, emails, and addresses of all CitSci participants
        <span class="text-grey-800 font-weight-bold"><i>are not shared or made public</i></span> for any
        reason, with the following exceptions: (1) project coordinators are provided
        access to their project participant names and emails so that they may communicate
        with their project participants on an as-needed basis, (2) the master participant
        list may be shared with the CitSci staff for internal programmatic research purposes,
        and (3) the names of individual contributors of observations may be listed in an
        effort to applaud and recognize the hard work and dedication of those participating in
        CitSci projects. This may include listing the name of the volunteer who
        submitted an observation and/or the sharing of data and/or pictures submitted
        by volunteers as per our data use policy. Such sharing is solely for the
        purpose of management and operations of CitSci and CitSci projects. Sharing
        of this information is only to support the CitSci mission of empowering
        the efforts of citizen science projects and their volunteers. Subscription
        to our e-newsletter is voluntary and entirely up to each participant. Other
        than the uses listed above, we do not share, sell, rent, or lease any of your
        specific personal information including name, phone, address, and email with anyone.
      </p>

      <h2>Collaboration and Data Sharing</h2>

      <p>As a policy, CitSci collaborates with several citizen science platforms to
        help you do great science, amplify outcomes, and help advance the field of
        citizen science. Specifically, we collaborate with
        <b-link href="https://scistarter.org/"
                target="NewWindow">SciStarter
        </b-link>
        so you can leverage their marketing
        and social network tools and the
        <b-link href="https://www.zooniverse.org/"
                target="NewWindow">Zooniverse
        </b-link>
        so you can share photos and tap into
        a large crowd of willing volunteers interested in image classification tasks.
      </p>

      <p>You can choose to use our platform integrations on an <span class="text-grey-800 font-weight-bold"><i>
            opt in </i></span> or <span class="text-grey-800 font-weight-bold"><i>opt-out</i></span> basis.</p>

      <h4>SciStarter</h4>

      <p>If you as a CitSci project manager opts to share your project details with
        SciStarter, then our policy is to share the names and emails of <span class="text-grey-800 font-weight-bold"><i>
            project managers only</i></span>, and essential project details (metadata) such as title,
        description, purpose, goals, topics, participant tasks, image and profile URLs,
        and keywords. Sharing this basic project information across databases facilitates the broader
        understanding about citizen science globally. However, SciStarter <i>
          shall not share this information with anyone, for any purpose</i> except as in
        accordance with their privacy policy to help recruit participants and advance science and
        research. We default this option to '<i> publish on SciStarter</i>'
        unless a project manager opts-out for this free integration
        service.
      </p>

      <h4>Zooniverse</h4>

      <p>If you as a CitSci project manager opts to share your project photos with the
        Zooniverse, then our policy is to share the name and email of you as project
        manager only along with the photos you select to be shared on the Zooniverse platform.
        The photos you share are shared as links (URLs) to your photos on CitSci so that
        Zooniverse volunteers can view and classify the shared photos.
      </p>

      <h2>Email Communications From CitSci</h2>

      <p>We may send you periodic information via email which we feel may be of interest
        to you only if you opt-in for such email communications. We do not share your
        personal information with any third parties in connection with our email
        communications. Please note that we may use a third party service provider to
        administer email lists for our e-newseltters and related communications. Any
        such provider may not use your email information for any purpose other than
        with helping facilitate our email communications with you.
      </p>

      <h2>Notification of Changes</h2>

      <p>Any changes in our Privacy Policy will be posted to this website and will become
        effective as of the date of posting with respect to information we then collect
        in the future, but will not be changed with respect to information that we have
        then already collected. We will notify you of such changes made to our privacy
        policy via our email communication channels.
      </p>

      <h2>Security</h2>

      <p>Wherever your personal information may be held within CitSci.org or on its
        behalf, we intend to take reasonable and appropriate steps to protect the
        information that you share with us from unauthorized access or disclosure.</p>

      <h2>Tracking Technologies</h2>

      <p>Tracking technologies may record information such as Internet domain and host
        names; Internet protocol (IP) addresses; browser software and operating system
        types; clock stream patterns; and dates and times that our site is accessed.
        Our use of tracking technologies allows us to analyze trends and statistics
        to improve our website and your Web experience. Data used from tracking
        technologies is not linked to Web users’ personal information.</p>

      <h2>Third Party Services</h2>

      <p>We disclose your information to third parties only as reasonably required
        to protect the rights and safety of our members, our organization, our
        employees, and users as you expressly permit and as required by law. Third
        party service providers are an important means by which CitSci maintains its
        website and mailing lists. We will take reasonable steps to ensure that
        these service providers are obligated to protect personal information on our
        behalf. We may transfer your information in the event of a merger,
        consolidation or acquisition. However, in such an event, any acquirer will
        be subject to the provisions of our commitments to you and this privacy policy.
      </p>

      <h2>Disclaimer</h2>

      <p>The information provided in this privacy statement should NOT be construed
        as giving business, legal, or other advice, or as warranting the security of
        information provided through this website</p>

      <br/>

      <p>Please
        <b-link to="contact">contact us</b-link>
        with any
        questions you may have regarding this privacy policy.
      </p>
    </div>
  </div>
</template>

<script>
export default {
  created() {
  },
  computed: {},
  methods: {},
}
</script>
